import React, { useEffect, useRef, useState } from 'react';

// A `url` that points at an actual video file (local, e.g. /videos/foo.mp4,
// or any direct .mp4/.webm/.mov/.ogg link) needs a native <video> element --
// an <iframe src="something.mp4"> doesn't reliably play back across
// browsers, since iframes are built for embeddable *pages* like a YouTube
// watch URL, not raw media files. Anything else (YouTube/Vimeo embed URLs,
// QuickSight embed URLs, etc.) keeps using the iframe as before.
const isLocalVideoFile = (url = '') => /\.(mp4|webm|mov|ogg)(\?.*)?$/i.test(url);

// Specifically a YouTube embed (as opposed to Vimeo/QuickSight/etc iframes)
// -- used to opt into the YouTube IFrame Player API below, since that's the
// only way to know when playback actually starts inside a cross-origin
// iframe (a plain <iframe> gives no onPlay-equivalent event of its own).
const isYouTubeEmbedUrl = (url = '') =>
  /^https?:\/\/(www\.)?(youtube(-nocookie)?\.com)\/embed\//i.test(url);

// Adds the params the YouTube IFrame Player API needs to attach to an
// *existing* iframe (rather than creating its own) -- enablejsapi is
// required, origin is recommended so the API's postMessage traffic is
// scoped to this exact page. See "Example: using an existing iframe" at
// https://developers.google.com/youtube/iframe_api_reference.
const withYouTubeJsApiParams = (url) => {
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('enablejsapi', '1');
    if (typeof window !== 'undefined') {
      parsed.searchParams.set('origin', window.location.origin);
    }
    return parsed.toString();
  } catch {
    return url;
  }
};

// The YT.Player constructor isn't available until the IFrame Player API
// script has loaded and called window.onYouTubeIframeAPIReady -- loaded
// lazily (once per page, shared across every VideoEmbed instance) rather
// than unconditionally, since most use cases don't embed YouTube at all.
let youTubeIframeApiPromise = null;
const loadYouTubeIframeApi = () => {
  if (window.YT?.Player) {
    return Promise.resolve(window.YT);
  }
  if (youTubeIframeApiPromise) {
    return youTubeIframeApiPromise;
  }
  youTubeIframeApiPromise = new Promise((resolve) => {
    const previousCallback = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previousCallback?.();
      resolve(window.YT);
    };
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(script);
  });
  return youTubeIframeApiPromise;
};

// +/-10s skip -- arrow keys on desktop, double-tap/double-click the
// left or right half of the video on mobile (and desktop, for free).
const SKIP_SECONDS = 10;
const DOUBLE_TAP_MS = 300;

// "X min demo" corner badge, generated from the video's own duration
// instead of a hand-typed string on each use case -- a hardcoded "3 min
// demo" was showing on every video regardless of its real length (one was
// actually ~5 min, another ~2 min). Rounds to the nearest minute, with a
// floor of 1 so a sub-60s clip still reads as "1 min demo" rather than
// "0 min demo".
const formatDurationBadge = (seconds) => {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return null;
  }
  const minutes = Math.max(1, Math.round(seconds / 60));
  return `${minutes} min demo`;
};

const VideoEmbed = ({
  url,
  title = 'Video',
  className = '',
  // Lets a caller crop out letterboxing/black bars baked into a specific
  // video by giving the box a slightly shorter aspect ratio than the
  // standard 16:9 -- paired with object-cover on the <video> below, the
  // extra bit of vertical zoom pushes those bars outside the visible box
  // instead of shrinking the actual picture. Leave the default alone
  // unless a particular video actually has that problem.
  aspectClassName = 'aspect-video',
  // Opt-in "3D" presentation: a slight tilt + shadow for depth, with a
  // clean outline around the video, a custom play button overlay, and an
  // optional corner badge + caption below. Purely a styling wrapper -- the
  // underlying <video> / <iframe> is unchanged.
  chrome = false,
  badge,
  caption,
}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  // Separate from isPlaying: isPlaying flips back to false on every pause,
  // which used to also un-flatten the 3D tilt each time -- so pausing
  // mid-watch snapped the card back to an angle. hasStarted latches true
  // the first time playback begins and never resets, so the card straightens
  // out on first play and then stays straight through any later pauses.
  const [hasStarted, setHasStarted] = useState(false);
  // Interacting with the native scrub bar pauses the <video> immediately,
  // *before* the 'seeking' event fires -- confirmed by logging the actual
  // event order, which is pause -> seeking -> seeked. So by the time
  // 'seeking' fires, videoRef.current.paused is already true and there's
  // no way to tell from that alone whether this is a real pause or the
  // start of a seek. Instead: isPlayingRef mirrors whether the video is
  // "supposed" to be playing (kept in sync with isPlaying, but readable
  // synchronously inside these handlers without waiting on React state).
  // onPause stashes that value in scrubResumeRef and starts a short timer
  // that will commit to "this was a real pause" if nothing else claims it;
  // onSeeking cancels that timer (since a seek did follow, so the pause
  // belongs to it), and onSeeked resumes playback if scrubResumeRef says
  // it should.
  const isPlayingRef = useRef(false);
  const isSeekingRef = useRef(false);
  const scrubResumeRef = useRef(false);
  const pauseClassifyTimeoutRef = useRef(null);
  // Double-tap/double-click skip: lastTapAtRef remembers when each side was
  // last tapped so a second tap within DOUBLE_TAP_MS counts as a double;
  // skipFlash drives the brief "-10s"/"+10s" bubble so the gesture is
  // discoverable (there's no other visible affordance for it).
  const lastTapAtRef = useRef({ left: 0, right: 0 });
  const flashHideTimeoutRef = useRef(null);
  const flashClearTimeoutRef = useRef(null);
  const [skipFlash, setSkipFlash] = useState(null); // { side, key } | null
  const [flashVisible, setFlashVisible] = useState(false);
  // Filled in from the video's real duration once its metadata loads (see
  // formatDurationBadge) -- null until then, so the corner badge just
  // doesn't render for that first moment rather than showing a guess.
  const [autoDurationBadge, setAutoDurationBadge] = useState(null);
  // object-cover fills whatever box the video sits in -- right for the
  // embedded player (it's also how the `aspectClassName` trick crops out
  // letterboxing baked into a specific file), but wrong once the browser
  // puts this same element into true fullscreen: there it fills the whole
  // screen and keeps cropping to cover it, cutting off picture that should
  // be visible. Switch to object-contain (show the whole frame, letterbox
  // if needed) only while actually in fullscreen.
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Stable per-instance id so the YouTube IFrame Player API can attach to
  // this exact <iframe> element (see the effect below) -- generated once
  // via useState's lazy initializer rather than on every render.
  const [youtubeElementId] = useState(
    () => `yt-embed-${Math.random().toString(36).slice(2, 10)}`
  );
  const youtubePlayerRef = useRef(null);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fsEl = document.fullscreenElement || document.webkitFullscreenElement;
      setIsFullscreen(fsEl != null && fsEl === videoRef.current);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Only the local <video> element has a real onPlay event (below). A
  // YouTube iframe is cross-origin, so the only way to know playback
  // actually started -- and flip the tilted "chrome" card back flat, same
  // as a local video does -- is the IFrame Player API's onStateChange.
  const isYouTube = isYouTubeEmbedUrl(url);

  useEffect(() => {
    if (!isYouTube) {
      return;
    }

    let isCancelled = false;

    loadYouTubeIframeApi().then((YT) => {
      if (isCancelled) {
        return;
      }
      youtubePlayerRef.current = new YT.Player(youtubeElementId, {
        events: {
          onStateChange: (event) => {
            if (event.data === YT.PlayerState.PLAYING) {
              setHasStarted(true);
            }
          },
        },
      });
    });

    return () => {
      isCancelled = true;
      youtubePlayerRef.current?.destroy?.();
      youtubePlayerRef.current = null;
    };
  }, [isYouTube, youtubeElementId]);

  if (!url) {
    return null;
  }

  const isLocal = isLocalVideoFile(url);

  const handlePlayClick = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const showSkipFlash = (side) => {
    setSkipFlash({ side, key: Date.now() });
    setFlashVisible(true);
    clearTimeout(flashHideTimeoutRef.current);
    flashHideTimeoutRef.current = setTimeout(() => setFlashVisible(false), 350);
    clearTimeout(flashClearTimeoutRef.current);
    flashClearTimeoutRef.current = setTimeout(() => setSkipFlash(null), 650);
  };

  const seekBy = (deltaSeconds, side) => {
    const el = videoRef.current;
    if (!el || !Number.isFinite(el.duration)) {
      return;
    }
    el.currentTime = Math.min(Math.max(el.currentTime + deltaSeconds, 0), el.duration);
    showSkipFlash(side);
  };

  // Shared by both the mouse dblclick case and a touch double-tap -- one
  // 'click' fires per tap on touch devices too, so timing consecutive
  // clicks against DOUBLE_TAP_MS covers both without separate touch
  // handlers (and without fighting the browser's own double-click-to-
  // fullscreen behavior, which this overlay intercepts before it reaches
  // the <video> underneath).
  const handleZoneTap = (side) => {
    const now = Date.now();
    const elapsed = now - lastTapAtRef.current[side];
    if (elapsed < DOUBLE_TAP_MS) {
      lastTapAtRef.current[side] = 0;
      seekBy(side === 'left' ? -SKIP_SECONDS : SKIP_SECONDS, side);
    } else {
      lastTapAtRef.current[side] = now;
    }
  };

  const handleVideoKeyDown = (e) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      seekBy(SKIP_SECONDS, 'right');
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      seekBy(-SKIP_SECONDS, 'left');
    }
  };

  // Left/right double-tap zones, stopping short of the bottom of the box
  // so the native scrub bar/controls strip underneath stays clickable --
  // only rendered for a real <video> (skipping doesn't mean anything for
  // an iframe embed). z-10 keeps it under the chrome-mode play button
  // overlay (z-20, only present before playback starts) so the very first
  // tap still starts the video instead of being swallowed as a skip.
  const skipZones = isLocal && (
    <div className="absolute inset-x-0 top-0 bottom-12 z-10 flex">
      <button
        type="button"
        tabIndex={-1}
        aria-label={`Rewind ${SKIP_SECONDS} seconds`}
        onClick={() => handleZoneTap('left')}
        className="h-full w-1/2 cursor-default bg-transparent"
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label={`Forward ${SKIP_SECONDS} seconds`}
        onClick={() => handleZoneTap('right')}
        className="h-full w-1/2 cursor-default bg-transparent"
      />

      {skipFlash && (
        <div
          key={skipFlash.key}
          className={`pointer-events-none absolute inset-y-0 flex w-1/2 items-center justify-center transition-opacity duration-300 ${
            skipFlash.side === 'left' ? 'left-0' : 'right-0'
          } ${flashVisible ? 'opacity-100' : 'opacity-0'}`}
        >
          <span className="rounded-full bg-black/70 px-4 py-2 text-sm font-semibold text-white">
            {skipFlash.side === 'left' ? `« ${SKIP_SECONDS}s` : `${SKIP_SECONDS}s »`}
          </span>
        </div>
      )}
    </div>
  );

  const media = isLocal ? (
    <video
      ref={videoRef}
      className={`h-full w-full bg-black ${isFullscreen ? 'object-contain' : 'object-cover'} ${chrome ? '' : 'rounded-xl'}`}
      src={url}
      title={title}
      controls={!chrome || isPlaying}
      playsInline
      onKeyDown={handleVideoKeyDown}
      onLoadedMetadata={() => {
        setAutoDurationBadge(formatDurationBadge(videoRef.current?.duration));
      }}
      onPlay={() => {
        isPlayingRef.current = true;
        setIsPlaying(true);
        setHasStarted(true);
      }}
      onPause={() => {
        if (isSeekingRef.current) {
          // 'seeking' already fired and claimed this -- shouldn't normally
          // reach here given the observed pause -> seeking -> seeked
          // order, but kept as a guard.
          return;
        }
        const wasPlaying = isPlayingRef.current;
        isPlayingRef.current = false;
        // Grabbing/clicking the native scrub bar pauses playback right
        // away, before 'seeking' fires -- so right now there's no way to
        // tell whether this is the start of a seek or a real pause (the
        // user hitting pause). Stash whether it was playing and give
        // 'seeking' ~250ms to claim this pause as its own; if nothing
        // shows up in that window, commit to it being a real pause.
        scrubResumeRef.current = wasPlaying;
        clearTimeout(pauseClassifyTimeoutRef.current);
        pauseClassifyTimeoutRef.current = setTimeout(() => {
          scrubResumeRef.current = false;
          setIsPlaying(false);
        }, 250);
      }}
      onSeeking={() => {
        isSeekingRef.current = true;
        // A seek did follow, so the preceding pause (if any) belongs to
        // it -- cancel the "turns out to be a real pause" timer from
        // onPause so isPlaying doesn't flip false mid-scrub.
        clearTimeout(pauseClassifyTimeoutRef.current);
      }}
      onSeeked={() => {
        isSeekingRef.current = false;
        const el = videoRef.current;
        const shouldResume = scrubResumeRef.current;
        scrubResumeRef.current = false;
        if (!shouldResume || !el || !el.paused) {
          return;
        }
        // Calling play() the instant the seek lands can be a no-op if the
        // browser hasn't buffered enough past the new position yet.
        // readyState >= 3 (HAVE_FUTURE_DATA) means there's enough to
        // actually start; otherwise wait for 'canplay' instead of
        // guessing with a timeout. Log a rejected play() (e.g. the
        // browser refusing it as not a real user gesture) instead of
        // failing silently.
        const resume = () => {
          isPlayingRef.current = true;
          videoRef.current?.play().catch((err) => {
            isPlayingRef.current = false;
            console.warn('[VideoEmbed] resume after seek failed:', err);
          });
        };
        if (el.readyState >= 3) {
          resume();
        } else {
          el.addEventListener('canplay', resume, { once: true });
        }
      }}
    />
  ) : (
    <iframe
      id={isYouTube ? youtubeElementId : undefined}
      className={`h-full w-full ${chrome ? '' : 'rounded-xl'}`}
      src={isYouTube ? withYouTubeJsApiParams(url) : url}
      title={title}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  );

  if (!chrome) {
    return (
      <div className={`relative ${aspectClassName} overflow-hidden ${className}`}>
        {media}
        {skipZones}
      </div>
    );
  }

  return (
    <div className={className}>
      <div
        className="overflow-hidden rounded-xl border-[3px] border-black/70 bg-white shadow-2xl ring-1 ring-white/20 transition-transform duration-500 ease-out"
        style={{
          // transformOrigin pinned to the top: with the default center
          // origin, the pivot sits inside the (much taller) video area, so
          // the thin caption strip at the bottom -- being short and close
          // to that pivot -- barely rotates and reads as flat even though
          // the video above it is visibly tilted. Anchoring the rotation
          // at the top keeps the top edge put and swings the whole rest of
          // the card (including the caption) forward together, so the
          // tilt is visible all the way down to the bottom edge.
          //
          // Angles bumped up from the original 3deg/-5deg -- at that size
          // the tilt read as barely-there, especially on the caption strip.
          // This matches the reference "tilted laptop screen" mock: a
          // clearly readable 3D lean, not just a hint of one.
          transformOrigin: 'top center',
          transform: hasStarted
            ? 'perspective(1600px) rotateX(0deg) rotateY(0deg)'
            : 'perspective(1600px) rotateX(6deg) rotateY(-18deg)',
        }}
      >
        <div className={`relative ${aspectClassName}`}>
          {media}
          {skipZones}

          {isLocal && !isPlaying && (
            <button
              type="button"
              onClick={handlePlayClick}
              aria-label={`Play ${title}`}
              className="absolute inset-0 z-20 flex items-center justify-center bg-black/10 transition hover:bg-black/20"
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-blue-600 shadow-lg ring-4 ring-white/20">
                <svg viewBox="0 0 24 24" className="ml-1 h-7 w-7 fill-white" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            </button>
          )}

          {/* badge, if the caller passed one, always wins (e.g. something
              that isn't a duration at all); otherwise fall back to the
              length read straight off the video itself, so it can't drift
              out of sync with the actual file the way a hand-typed "3 min
              demo" did. */}
          {(badge || autoDurationBadge) && isLocal && !isPlaying && (
            <span className="absolute right-4 top-4 z-20 rounded-full bg-black/60 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
              {badge || autoDurationBadge}
            </span>
          )}
        </div>

        {caption && (
          <p className="border-t-[3px] border-black/70 bg-gray-100 py-2 text-center text-sm font-medium text-black/70">
            {caption}
          </p>
        )}
      </div>
    </div>
  );
};

export default VideoEmbed;
